import type { JSX } from 'preact';
import {
  MAX_WIND_SPEED,
  TIME_OF_DAY_PRESETS,
  timeOfDayToPreset,
  timePresetPatch,
  type Environment,
  type EnvironmentPatch,
  type TimeOfDayPreset,
} from '../../sim/map/index.ts';
import { NumberField, Select, Toggle } from '../controls.tsx';
import './environment.css';

export type EnvironmentEditorProps = {
  environment: Environment;
  onChange: (patch: EnvironmentPatch) => void;
};

/**
 * A weather channel whose renderer was removed. It stays as a disabled placeholder — no inputs,
 * cannot be turned on — until its system is rebuilt. See docs/CONTENT-VERSIONING.md (v16).
 */
function ParkedChannel({ title }: { title: string }): JSX.Element {
  return (
    <div class="editor-environment-section">
      <Toggle
        label={title}
        value={false}
        disabled
        onChange={() => {}}
        hint="Not built yet. This channel does nothing and will return as its own system."
      />
    </div>
  );
}

/**
 * Authoring for the map environment. Wind and time of day are the only live channels: wind is a
 * squared, capped downwind shift of the movement circle, and time of day grades the sky. The
 * rest are disabled placeholders until their systems are rebuilt.
 */
export function EnvironmentEditor({
  environment,
  onChange,
}: EnvironmentEditorProps): JSX.Element {
  const { timeEnabled, wind } = environment;

  return (
    <div class="editor-environment">
      <div class="editor-environment-section">
        <Toggle
          label="Set time of day"
          value={timeEnabled}
          onChange={(enabled) => onChange({ timeEnabled: enabled })}
          hint="Off is noon. On grades the sky to the chosen time."
        />
        {timeEnabled ? (
          <Select<TimeOfDayPreset>
            label="Time"
            value={timeOfDayToPreset(environment.timeOfDay)}
            options={TIME_OF_DAY_PRESETS.map((preset) => ({
              value: preset.id,
              label: preset.label,
            }))}
            onChange={(id) => onChange(timePresetPatch(id))}
          />
        ) : null}
      </div>

      <div class="editor-environment-section">
        <Toggle
          label="Enable wind"
          value={wind.enabled}
          onChange={(enabled) => onChange({ wind: { enabled } })}
          hint="A small downwind shift of the movement circle. Stronger at rest, capped in a storm."
        />
        {wind.enabled ? (
          <>
            <NumberField
              label="Direction"
              value={Math.round(wind.directionDeg)}
              min={0}
              max={359}
              step={15}
              onChange={(directionDeg) => onChange({ wind: { directionDeg } })}
            />
            <NumberField
              label="Meters per second"
              value={wind.speed}
              min={0}
              max={MAX_WIND_SPEED}
              step={0.5}
              onChange={(speed) => onChange({ wind: { speed } })}
            />
            <p class="ui-hint">
              Direction is the compass bearing the wind blows toward (0 = north). Speed is metres
              per second — the same number the play HUD shows. Light breeze barely moves the disk;
              stronger wind grows with speed squared.
            </p>
          </>
        ) : null}
      </div>

      <ParkedChannel title="Clouds" />
      <ParkedChannel title="Rain" />
      <ParkedChannel title="Chop" />
      <ParkedChannel title="Fog" />
    </div>
  );
}
